import { useCallback, useReducer } from 'react';
import { api, errMsg } from '../api.js';
import { toast } from '../components/Toasts.jsx';
import { refreshNavCounts } from './useNavCounts.js';

// T270/T227 — the recommendation feed: the rows, the paging cursor, and the
// status writes that move rows in and out of it.
//
// This exists because of one invariant, established by T220 and then broken
// twice more before T253 caught it: **the list is a WINDOW over a much larger
// pool, and every write that crosses the dismissed boundary has to move the
// cursor with it.**
//
// The API's list endpoint excludes `dismissed` and pages with `.skip(offset)`.
// So an optimistic dismiss SHORTENS the pool the server counts over while our
// offset stays put — twenty dismisses, then "Load more", and the server skips
// twenty rows that are still there. They never arrive, `hasMore` goes false,
// and the UI calls a partial list complete.
//
// While that arithmetic lived inline in `updateRecStatus`, every new call site
// that wrote a status (bulk apply, bulk dismiss, un-dismiss, explain) either
// re-derived it or — more often — reached for a full `refresh()`, which hid the
// problem by throwing away every page the operator had loaded. A reducer keeps
// the rows and the cursor in one place so they cannot be updated separately.

export function crossedDismissBoundary(from, to) {
  return (from === 'dismissed') !== (to === 'dismissed');
}

/** Signed cursor delta for a status move: -1 leaves the pool, +1 rejoins it. */
export function cursorDelta(from, to) {
  if (!crossedDismissBoundary(from, to)) return 0;
  return to === 'dismissed' ? -1 : 1;
}

const INITIAL = {
  recs: [],
  loaded: false,
  offset: 0, // rows already consumed from the server's paging, NOT recs.length:
  // rows also arrive from the source-filter fetch, and counting those as pages
  // consumed would make the next Load-more skip real rows.
  total: 0,
  hasMore: false,
};

export function feedReducer(state, action) {
  switch (action.type) {
    case 'loaded': {
      const rows = action.rows ?? [];
      return { recs: rows, loaded: true, offset: rows.length, total: action.total ?? rows.length, hasMore: Boolean(action.hasMore) };
    }
    case 'appended': {
      // De-duped by id: a row can move between pages when a scan re-scores it
      // mid-session, and the sort key is the score.
      const seen = new Set(state.recs.map((r) => r.id));
      const fresh = (action.rows ?? []).filter((r) => !seen.has(r.id));
      return {
        ...state,
        recs: state.recs.concat(fresh),
        // The server advanced by what it SENT, including the duplicates we
        // dropped — advancing by `fresh.length` would re-request them forever.
        offset: state.offset + (action.rows?.length ?? 0),
        total: action.total ?? state.total,
        hasMore: Boolean(action.hasMore),
      };
    }
    case 'restored': {
      // A row coming back from the Dismissed panel REJOINS the server's pool,
      // so `total` grows — but NOT the paging cursor, and the difference is the
      // whole bug this case exists to avoid.
      //
      // `offset` means "server rows already consumed". A dismiss can only
      // target a row inside the loaded window, so -1 is always right. An
      // un-dismiss restores a row at an unknown score position: the panel lists
      // rows dismissed in earlier sessions, which never produced a -1 and are
      // mostly low-scoring, so they land BELOW the cursor. Advancing it then
      // makes the next page start one row late, and the row at that index is
      // never fetched — a permanent hole, invisible because the restored row is
      // put on screen locally.
      //
      // The asymmetry decides the safe direction: an offset that is too LOW
      // only costs a duplicate, which 'appended' de-dupes by id. Too HIGH loses
      // a row for the session. So the cursor stays put.
      // A row dismissed in THIS session is still in `recs`, carrying
      // status:'dismissed' — the available list filters it out, it is not
      // absent. Concatenating only the rows we do not hold therefore restored
      // nothing for the commonest case: the row stayed dismissed, stayed
      // hidden, and `total` grew anyway, so it vanished from the UI entirely.
      // Patch what we hold, append what we do not.
      const byId = new Map((action.rows ?? []).map((r) => [r.id, r]));
      const recs = state.recs.map((r) => (byId.has(r.id) ? { ...r, ...byId.get(r.id) } : r));
      const held = new Set(state.recs.map((r) => r.id));
      const fresh = (action.rows ?? []).filter((r) => !held.has(r.id));
      return {
        ...state,
        recs: recs.concat(fresh),
        // It rejoined the server's pool either way, so the pool total grows.
        total: state.total + (action.rows?.length ?? 0),
      };
    }
    case 'merged': {
      // Extra rows pulled in by a filter, outside the paging cursor.
      const seen = new Set(state.recs.map((r) => r.id));
      const fresh = (action.rows ?? []).filter((r) => !seen.has(r.id));
      return fresh.length ? { ...state, recs: state.recs.concat(fresh) } : state;
    }
    case 'patched':
      return {
        ...state,
        recs: state.recs.map((r) => (r.id === action.rec.id ? { ...r, ...action.rec } : r)),
      };
    case 'status': {
      // The only place the cursor moves on a write.
      const delta = cursorDelta(action.from, action.to);
      return {
        ...state,
        recs: state.recs.map((r) => (r.id === action.id ? { ...r, ...action.fields } : r)),
        offset: Math.max(0, state.offset + delta),
        total: Math.max(0, state.total + delta),
      };
    }
    default:
      return state;
  }
}

export function useRecommendationsFeed() {
  const [feed, dispatch] = useReducer(feedReducer, INITIAL);

  const load = useCallback(async () => {
    // limit=100 is the API's per-page maximum, so this is the FIRST page, not
    // the whole list. T182 added total/hasMore for exactly this.
    const body = await api.get('/recommendations?limit=100&offset=0');
    dispatch({ type: 'loaded', rows: body.recommendations ?? [], total: body.total, hasMore: body.hasMore });
    return body;
  }, []);

  const loadMore = useCallback(async (offset) => {
    const body = await api.get(`/recommendations?limit=100&offset=${offset}`);
    dispatch({ type: 'appended', rows: body.recommendations ?? [], total: body.total, hasMore: body.hasMore });
  }, []);

  const mergeRows = useCallback((rows) => dispatch({ type: 'merged', rows }), []);
  const patchRec = useCallback((rec) => dispatch({ type: 'patched', rec }), []);
  const applyStatus = useCallback((id, from, to, fields) => dispatch({ type: 'status', id, from, to, fields }), []);
  const restoreRows = useCallback((rows) => dispatch({ type: 'restored', rows }), []);

  return { feed, dispatch, load, loadMore, mergeRows, patchRec, applyStatus, restoreRows };
}

/**
 * Write one status, optimistically, keeping the window honest.
 *
 * Extracted so the four call sites cannot drift on the rollback or the cursor.
 * `deps` is everything it needs from the page — passed in rather than reached
 * for, so this stays testable without a DOM.
 */
export async function writeRecStatus({ id, status, opts = {}, feed, applyStatus, setActionPending, setRecStats, bumpStatusCount, onUndo }) {
  const prev = feed.recs.find((r) => r.id === id);
  // `opts.from` is the caller stating the row's CURRENT status outright.
  //
  // The Undo action lives inside a toast, so it fires from the closure of the
  // render that created it — and in that render's `feed` the row still carries
  // its pre-dismiss status. Deriving "from" out of that snapshot gives
  // from === to, which is a no-op for the cursor delta AND for bumpStatusCount,
  // while the row's status is still written. The screen looks correct and the
  // window is quietly one row short, permanently, per undone dismiss.
  const previousStatus = opts.from ?? prev?.status ?? 'new';
  const previousReason = prev?.dismissedReason ?? '';

  setActionPending?.(id);
  applyStatus(id, previousStatus, status, {
    status,
    dismissedReason: status === 'dismissed' ? (opts.reason ?? '') : '',
  });
  try {
    const body = { status };
    if (status === 'dismissed' && typeof opts.reason === 'string') body.dismissedReason = opts.reason;
    await api.patch(`/recommendations/${id}`, body);
    // The sidebar badge and the status pills read from the stats endpoint,
    // which a local patch cannot update. One cheap call, not four.
    refreshNavCounts();
    setRecStats?.((st) => bumpStatusCount(st, previousStatus, status));
    if (!opts.silent) {
      const label =
        status === 'dismissed' ? 'Dismissed'
          : status === 'applied' ? 'Marked applied'
            : status === 'shortlisted' ? 'Shortlisted'
              : 'Updated';
      // T253 — Undo covers every triage key, not just dismiss: `a` sits between
      // `s` and `d`, and an accidental apply cost four steps through the tracker.
      toast(label, {
        kind: 'success',
        action: { label: 'Undo', onClick: () => onUndo?.(id, previousStatus, status) },
      });
    }
    return true;
  } catch (e) {
    // Put the row back exactly as it was — an optimistic update that cannot
    // roll back is just a lie with better latency. Reversing the from/to also
    // reverses the cursor delta, so the two can never fall out of step.
    applyStatus(id, status, previousStatus, { status: previousStatus, dismissedReason: previousReason });
    toast(errMsg(e, 'Could not update that recommendation'), { kind: 'error' });
    return false;
  } finally {
    setActionPending?.(null);
  }
}
